import { useCallback, useState } from 'react';
import { isAfter, isBefore, parseISO } from 'date-fns';
import { BookingRewireDataType } from '../../../../Models/BookingRewireDataType';
import { getAllData } from '../../../../utils/helpers/fetching';
import { GET_BOOKING_ENTRIES_BY_BOOKING_ID } from '../../../../gql/bookingapi';

interface BookingEntryRow {
  bookingId: string;
  start: string;
  end: string;
  dateFrom?: undefined;
  inventoryId?: string;
  resource?: string;
  equipment?: { inventoryId?: string };
  bookingEntries?: BookingEntryRow[];
  [key: string]: unknown;
}

type EventInput = BookingRewireDataType | BookingEntryRow;

const isEntryRow = (data: EventInput): data is BookingEntryRow =>
  "bookingId" in data && !("dateFrom" in data && data.dateFrom) && "start" in data;

const isSplitEvent = (data: EventInput): data is BookingRewireDataType & { bookingId: string } =>
  "bookingId" in data && typeof (data as Record<string, unknown>).bookingId === "string"
  && (data as Record<string, unknown>).bookingId !== data.id;

const normalizeToBooking = (data: EventInput): BookingRewireDataType => {
  if (isEntryRow(data)) {
    return {
      ...(data as Record<string, unknown>),
      id: data.bookingId,
      dateFrom: data.start,
      dateTo: data.end,
      bookingEntries: (data.bookingEntries ?? [data]).map((entry) => ({
        ...entry,
        inventoryId: entry.inventoryId ?? entry.resource ?? entry.equipment?.inventoryId,
        start: entry.start,
        end: entry.end,
      })),
    } as BookingRewireDataType;
  }
  if (isSplitEvent(data)) {
    return { ...data, id: data.bookingId };
  }
  return data;
};

const pickScopedEntries = (
  entries: Array<Record<string, unknown>>,
  clickedStart?: string,
  clickedEnd?: string,
) => {
  const now = new Date();
  const isEntryOngoing = (entry: Record<string, unknown>) => {
    const start = entry.start as string | undefined;
    const end = entry.end as string | undefined;
    if (!start || !end) {
      return false;
    }
    return isAfter(now, parseISO(start)) && isBefore(now, parseISO(end));
  };

  const ongoingEntries = entries.filter(isEntryOngoing);
  if (ongoingEntries.length > 0) {
    return ongoingEntries;
  }

  if (clickedStart && clickedEnd) {
    const clickedStartMs = parseISO(clickedStart).valueOf();
    const clickedEndMs = parseISO(clickedEnd).valueOf();
    const clickedSlotEntries = entries.filter((entry) => {
      const start = entry.start as string | undefined;
      const end = entry.end as string | undefined;
      if (!start || !end) {
        return false;
      }
      return parseISO(start).valueOf() === clickedStartMs && parseISO(end).valueOf() === clickedEndMs;
    });

    if (clickedSlotEntries.length > 0) {
      return clickedSlotEntries;
    }
  }

  return entries;
};

interface UseChangeEndTimeModalOptions {
  initialSelectedResourceId?: string | null;
  onSave?: (booking: BookingRewireDataType, newEndTime: string) => void;
  client?: {
    query?: (...args: unknown[]) => unknown;
  } | null;
}

const useChangeEndTimeModal = (options?: UseChangeEndTimeModalOptions) => {
  const resolvedOptions = options || {};
  const { initialSelectedResourceId = null, onSave, client = null } = resolvedOptions;

  const [isChangeEndTimeOpen, setIsChangeEndTimeOpen] = useState(false);
  const [bookingForEndTimeChange, setBookingForEndTimeChange] = useState<BookingRewireDataType | null>(null);

  const handleOpenChangeEndTimeModal = useCallback(async (params?: { data?: EventInput } | null) => {
    if (params?.data) {
      const normalizedBooking = normalizeToBooking(params.data);
      setBookingForEndTimeChange(normalizedBooking);

      const bookingId = (normalizedBooking as { id?: string })?.id;
      if (client && bookingId) {
        try {
          const { items } = await getAllData({
            client,
            query: GET_BOOKING_ENTRIES_BY_BOOKING_ID,
            drillData: true,
            fetchPolicy: "no-cache",
            dataPath: ["data", "bookingEntriesByBookingId"],
            variables: {
              bookingId,
              nextToken: null,
              filter: { active: { eq: true } },
            },
          });

          if (items?.length) {
            const fullEntries = items
              .map((entry: Record<string, unknown>) => {
                const equipment = entry.equipment as { inventoryId?: string } | undefined;
                const resolvedInventoryId =
                  (entry.resource as string | undefined) ||
                  (entry.inventoryId as string | undefined) ||
                  equipment?.inventoryId;

                return {
                  ...entry,
                  inventoryId: resolvedInventoryId,
                  resource: resolvedInventoryId,
                  start: entry.start,
                  end: entry.end,
                  id: entry.id,
                  bookingId: entry.bookingId || bookingId,
                };
              })
              .sort((a: { start?: string }, b: { start?: string }) =>
                String(a.start || "").localeCompare(String(b.start || ""))
              );

            const scopedEntries = pickScopedEntries(
              fullEntries,
              normalizedBooking.dateFrom,
              normalizedBooking.dateTo,
            );

            const earliestStart = scopedEntries[0]?.start;
            const latestEnd = scopedEntries[scopedEntries.length - 1]?.end;

            setBookingForEndTimeChange((previous) => {
              const baseBooking = previous && previous.id === bookingId ? previous : normalizedBooking;
              return {
                ...baseBooking,
                id: bookingId,
                bookingEntries: scopedEntries,
                // Keep the modal scoped to ongoing slot entries (or clicked slot fallback).
                dateFrom: earliestStart || baseBooking?.dateFrom,
                dateTo: latestEnd || baseBooking?.dateTo,
              } as BookingRewireDataType;
            });
          }
        } catch (_error) {
          // Keep modal usable with initial row data when entry refresh fails.
        }
      }
    }
    setIsChangeEndTimeOpen(true);
  }, [client]);

  const handleCloseChangeEndTimeModal = useCallback(() => {
    setIsChangeEndTimeOpen(false);
    setBookingForEndTimeChange(null);
  }, []);

  const handleSaveEndTime = useCallback((booking: BookingRewireDataType, newEndTime: string) => {
    if (onSave) {
      onSave(booking, newEndTime);
    }
    handleCloseChangeEndTimeModal();
  }, [onSave, handleCloseChangeEndTimeModal]);

  return {
    isChangeEndTimeOpen,
    bookingForEndTimeChange,
    handleOpenChangeEndTimeModal,
    handleCloseChangeEndTimeModal,
    handleSaveEndTime,
    initialSelectedResourceId,
  };
};

export default useChangeEndTimeModal;
